import { Link } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getUserFriendlyMessage } from '@/lib/error-handler';

interface ErrorFallbackProps {
  readonly error: Error | undefined;
  readonly onReset: () => void;
}

// eslint-disable-next-line sonarjs/function-return-type
export function ErrorFallback({ error, onReset }: ErrorFallbackProps): ReactNode {
  const { t } = useTranslation('common');

  return (
    <div className="flex min-h-[400px] items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <CardTitle>{t('errorBoundary.title', 'Something went wrong')}</CardTitle>
          </div>
          <CardDescription>
            {t(
              'errorBoundary.description',
              'An unexpected error occurred. Please try again or return to the home page.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm text-muted-foreground">
                {getUserFriendlyMessage(error, t('errorBoundary.unknownError', 'Unknown error'))}
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={onReset} variant="outline">
              {t('errorBoundary.tryAgain', 'Try Again')}
            </Button>
            <Button asChild>
              <Link to="/">{t('errorBoundary.goHome', 'Go Home')}</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
